import {
  createNewBranch,
  findByUser,
  getAllBranches,
  getAssignedBranchesToCompany,
  getAssignedBranchesToUser,
  getBranchById,
  getExistingBranch,
  getTotalActiveBranches,
  getUnassignedBranchesToCompany,
  getUnassignedBranchesToUser,
  listAllBranches,
  updateBranch,
  updateManyBranches,
} from '@/api/branch/branch';
import { ListAllBranch, TotalActiveBranch } from '@/api/entities/branch';
import { StructureAdvancedFilter } from '@/entities/structure/group';
import { TBranchSchema } from '@/schemas/branch/branch.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';
import { useAuth } from './auth.hooks';

export const useCreateBranch = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const { limitedRoleLoggedInUserId } = useAuth();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TBranchSchema) =>
      createNewBranch(data, {
        createdByUserId: limitedRoleLoggedInUserId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getAllBranches'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveBranches'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingBranch'] });
      queryClient.invalidateQueries({ queryKey: ['listAllBranches'] });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedBranchesToCompany'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedBranchesToUser'],
      });

      notify(t('common:branchCreatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useGetExistingBranch = (query?: TBranchSchema) => {
  return useQuery({
    queryKey: ['getExistingBranch', query],
    queryFn: () => getExistingBranch(query),
  });
};

export const useListAllBranches = (query?: ListAllBranch) => {
  const { limitedRoleLoggedInUserId } = useAuth();

  return useQuery({
    queryKey: ['listAllBranches', query],
    queryFn: () =>
      listAllBranches({ ...query, userId: limitedRoleLoggedInUserId }),
  });
};

export const useGetAllBranches = (
  filter: StructureAdvancedFilter,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc'
) => {
  return useQuery({
    queryKey: ['getAllBranches', filter, page, sortBy, sortDirection],
    queryFn: () => getAllBranches(filter, page, limit, sortBy, sortDirection),
  });
};

export const useGetTotalActiveBranches = (query?: TotalActiveBranch) => {
  return useQuery({
    queryKey: ['getTotalActiveBranches', query],
    queryFn: () => getTotalActiveBranches(query),
  });
};

export const useGetBranchById = (id: string) => {
  return useQuery({
    queryKey: ['getBranchById', id],
    queryFn: () => getBranchById(id),
    enabled: !!id,
  });
};

export const useGetUnassignedBranchesToCompany = () => {
  const { limitedRoleLoggedInUserId } = useAuth();

  return useQuery({
    queryKey: ['getUnassignedBranchesToCompany'],
    queryFn: () => getUnassignedBranchesToCompany(limitedRoleLoggedInUserId),
  });
};

export const useGetAssignedBranchesToCompany = (companyId: string) => {
  return useQuery({
    queryKey: ['getAssignedBranchesToCompany', companyId],
    queryFn: () => getAssignedBranchesToCompany(companyId),
    enabled: !!companyId,
  });
};

export const useGetUnassignedBranchesToUser = (userId: string) => {
  const { limitedRoleLoggedInUserId } = useAuth();

  return useQuery({
    queryKey: ['getUnassignedBranchesToUser', userId],
    queryFn: () =>
      getUnassignedBranchesToUser(userId, limitedRoleLoggedInUserId),
    enabled: !!userId,
  });
};

export const useGetAssignedBranchesToUser = (
  userId: string,
  roleTypeId: string
) => {
  return useQuery({
    queryKey: ['getAssignedBranchesToUser', userId, roleTypeId],
    queryFn: () => getAssignedBranchesToUser(userId, roleTypeId),
    enabled: !!userId && !!roleTypeId,
  });
};

export const useUpdateBranch = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (branch: TBranchSchema) => updateBranch(branch),
    onSuccess: (_, variables) => {
      const { id, companyId } = variables;

      queryClient.invalidateQueries({ queryKey: ['getAllGroups'] });
      queryClient.invalidateQueries({ queryKey: ['getAllCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['getAllBranches'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveBranches'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingBranch'] });
      queryClient.invalidateQueries({ queryKey: ['listAllBranches'] });
      queryClient.invalidateQueries({ queryKey: ['getBranchById', id] });

      queryClient.invalidateQueries({
        queryKey:
          companyId !== null
            ? ['getAssignedBranchesToCompany', companyId]
            : ['getAssignedBranchesToCompany'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedBranchesToCompany'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedBranchesToUser'],
      });

      notify(t('common:branchUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating branch:', error);
    },
  });
};

export const useUpdateManyBranches = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (branches: TBranchSchema[]) => updateManyBranches(branches),
    onSuccess: (_, variables) => {
      for (const item of variables) {
        queryClient.invalidateQueries({
          queryKey: ['getBranchById', item?.id],
        });
        queryClient.invalidateQueries({
          queryKey:
            item?.companyId !== null
              ? ['getAssignedBranchesToCompany', item?.companyId]
              : ['getAssignedBranchesToCompany'],
        });
      }

      queryClient.invalidateQueries({ queryKey: ['getAllGroups'] });
      queryClient.invalidateQueries({ queryKey: ['getAllCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['getAllBranches'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveBranches'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingBranch'] });
      queryClient.invalidateQueries({ queryKey: ['listAllBranches'] });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedBranchesToCompany'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedBranchesToUser'],
      });

      notify(t('common:branchUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating branches:', error);
    },
  });
};

export const useGetBranchesSelectOptions = (query?: ListAllBranch) => {
  const { data: allBranches, isLoading: isLoadingAllBranches } =
    useListAllBranches(query);

  const getBranchesSelectOptions = () => {
    if (!isLoadingAllBranches && allBranches && allBranches.data.length > 0) {
      return allBranches.data.map((group: TBranchSchema) => {
        const { id, name } = group;

        return {
          label: name,
          value: id,
        };
      });
    }

    return undefined;
  };

  return { getBranchesSelectOptions, isLoadingAllBranches };
};

export const useGetBranchByUser = (userId: string, roleName: string) => {
  return useQuery({
    queryKey: ['getBranchByGroupUser', userId, roleName],
    queryFn: () => findByUser(userId, roleName),
  });
};
